import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Tag, Space, Card, Typography, Switch } from 'antd';
import { VideoCameraOutlined, PlusOutlined, YoutubeOutlined } from '@ant-design/icons';
import api from '../utils/api';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

export default function VideoEditLog({ user }) {
  const [logs, setLogs] = useState([]);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => { fetchLogs(); fetchSongs(); }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/songs/edit-logs/all');
      setLogs(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchSongs = async () => {
    try {
      const res = await api.get('/songs', { params: { limit: 1000 } });
      setSongs(res.data.songs || []);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (values) => {
    try {
      await api.post(`/songs/${values.song_id}/edit-log`, {
        edit_type: values.edit_type,
        notes: values.notes,
        youtube_uploaded: values.youtube_uploaded,
        youtube_url: values.youtube_url
      });
      message.success('Edit log added!');
      setModalVisible(false);
      form.resetFields();
      fetchLogs();
    } catch (err) { message.error('Failed to add edit log'); }
  };

  const columns = [
    { title: 'Song', dataIndex: 'song_title', key: 'song_title', render: t => <strong>{t}</strong> },
    { title: 'Company', dataIndex: 'company_name', key: 'company_name' },
    { title: 'Editor', dataIndex: 'editor_name', key: 'editor_name' },
    { title: 'Type', dataIndex: 'edit_type', key: 'edit_type', render: t => <Tag color="blue">{t}</Tag> },
    { title: 'Notes', dataIndex: 'notes', key: 'notes', ellipsis: true },
    {
      title: 'YouTube', dataIndex: 'youtube_uploaded', key: 'yt',
      render: (v, r) => v ? (
        <Space>
          <Tag color="red"><YoutubeOutlined /> Uploaded</Tag>
          {r.youtube_url && <a href={r.youtube_url} target="_blank" rel="noreferrer">Link</a>}
        </Space>
      ) : <Tag>Not Uploaded</Tag>
    },
    { title: 'Date', dataIndex: 'created_at', key: 'date', render: d => new Date(d).toLocaleDateString('en-IN') },
  ];

  const canAdd = ['admin', 'manager', 'video_editor'].includes(user?.role);

  return (
    <Card>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3}><VideoCameraOutlined /> Video Edit Log</Title>
        {canAdd && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
            Add Edit Log
          </Button>
        )}
      </Space>

      <Table dataSource={logs} columns={columns} rowKey="id" loading={loading} pagination={{ pageSize: 20 }} />

      <Modal title="Add Video Edit Log" open={modalVisible} onCancel={() => setModalVisible(false)} footer={null} width={600}>
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item name="song_id" label="Song" rules={[{ required: true }]}>
            <Select showSearch placeholder="Select song" optionFilterProp="children">
              {songs.map(s => <Option key={s.id} value={s.id}>{s.title} ({s.company_name || 'N/A'})</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="edit_type" label="Edit Type" initialValue="video_edit">
            <Select>
              <Option value="video_edit">Video Edit</Option>
              <Option value="audio_mix">Audio Mix</Option>
              <Option value="lyric_video">Lyric Video</Option>
              <Option value="music_video">Music Video</Option>
              <Option value="short_video">Short/Reel</Option>
            </Select>
          </Form.Item>
          <Form.Item name="notes" label="Notes (Song name, details)">
            <TextArea rows={3} placeholder="Write what you edited, song name, details..." />
          </Form.Item>
          <Form.Item name="youtube_uploaded" label="Uploaded to YouTube?" valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
          <Form.Item name="youtube_url" label="YouTube URL (if uploaded)">
            <Input placeholder="https://youtube.com/watch?v=..." />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>Save Edit Log</Button>
        </Form>
      </Modal>
    </Card>
  );
}
